import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useAtsScorecardsApi, { useAtsScorecardResponsesApi } from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Tabs from '../../components/ui/Tabs';
import ScorecardTemplateDialog from './ScorecardTemplateDialog';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

const LinkCell = styled.span`
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const PlainCell = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`;

const fmtDateShort = (iso, lang) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(lang || 'tr'); } catch { return ''; }
};

export default function AtsScorecardsPage() {
  const { t, i18n } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();
  const writable = canWrite ? canWrite('AT') : false;

  const [activeTab, setActiveTab] = useState('responses');

  return (
    <Container>
      <Tabs
        tabs={[
          { key: 'responses', label: t('ats.scorecard.tab.responses') },
          { key: 'templates', label: t('ats.scorecard.tab.templates') },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'templates' && (
        <TemplatesView writable={writable} t={t} />
      )}
      {activeTab === 'responses' && (
        <ResponsesView t={t} lang={i18n.language} history={history} />
      )}
    </Container>
  );
}

// ─── Templates tab — original list ───────────────────────────────────────────
function TemplatesView({ writable, t }) {
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, id: null, name: '' });
  const [busy, setBusy] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, template: null });

  const { fetching, data, pagination, columns: baseColumns, remove, refresh } = useAtsScorecardsApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    return [{
      type: 'edit',
      label: t('common.edit'),
      onClick: () => setEditDialog({ open: true, template: row }),
    }, {
      type: 'delete',
      label: t('common.delete'),
      onClick: () => setDeleteDialog({ isOpen: true, id: row.id, name: row.name }),
    }];
  }, [t, writable]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <>
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.scorecard.deleteConfirm', { name: deleteDialog.name })}
        onConfirm={async () => { setBusy(true); await remove(deleteDialog.id); setDeleteDialog({ isOpen: false, id: null, name: '' }); setBusy(false); }}
        onCancel={() => setDeleteDialog({ isOpen: false, id: null, name: '' })}
        loading={busy}
      />

      <DataTable
        title={t('ats.scorecard.tab.templates')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        canCreateNewRecord={writable}
        newButtonLabel={t('ats.scorecard.form.newTemplate')}
        onNewButtonClick={() => setEditDialog({ open: true, template: null })}
      />

      <ScorecardTemplateDialog
        open={editDialog.open}
        template={editDialog.template}
        onClose={() => setEditDialog({ open: false, template: null })}
        onSaved={refresh}
      />
    </>
  );
}

// ─── Responses tab — relationship-aware list ─────────────────────────────────
function ResponsesView({ t, lang, history }) {
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const { fetching, data, pagination, columns: baseColumns } = useAtsScorecardResponsesApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const goToApplication = useCallback((applicationId) => {
    if (applicationId) history.push(`/ats/applications/${applicationId}`);
  }, [history]);

  const columns = useMemo(() => [...baseColumns.map((col) => {
    // Click-through: candidate + interview cells route to application detail
    // (no dedicated interview detail page exists yet). Job posting renders
    // as plain text since there's no posting detail page either.
    if (col.name === 'candidateFullName') {
      return {
        ...col,
        options: {
          ...col.options,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row?.candidateFullName) return '';
            return (
              <LinkCell onClick={() => goToApplication(row.applicationId)}>
                {row.candidateFullName}
              </LinkCell>
            );
          },
        },
      };
    }
    if (col.name === 'interviewSummary') {
      return {
        ...col,
        options: {
          ...col.options,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return '';
            const typeLabel = row.interviewType
              ? t(`ats.interview.type.${row.interviewType}`, { defaultValue: row.interviewType })
              : '';
            const dt = fmtDateShort(row.interviewScheduledStart, lang);
            const summary = [typeLabel, dt].filter(Boolean).join(' — ');
            return (
              <LinkCell onClick={() => goToApplication(row.applicationId)}>
                {summary || '-'}
              </LinkCell>
            );
          },
        },
      };
    }
    if (col.name === 'jobPostingTitle') {
      return {
        ...col,
        options: {
          ...col.options,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <PlainCell>{row?.jobPostingTitle || '-'}</PlainCell>;
          },
        },
      };
    }
    return col;
  }),
  // Panel debrief (SPEC F4) — cevabın mülakatının karşılaştırma + karar sayfası.
  // Görünürlük kuralı (anchoring) sayfanın ucundadır; burada daraltılmaz.
  {
    name: 'actions', label: ' ',
    options: {
      filter: false, sort: false, download: false, print: false,
      customBodyRender: (_, tableMeta) => {
        const row = data[tableMeta.rowIndex];
        if (!row?.interviewId) return null;
        return (
          <OptionsMenu
            row={row}
            actions={[{
              type: 'debrief',
              label: t('ats.debrief.action'),
              onClick: () => history.push(`/ats/interviews/${row.interviewId}/debrief`),
            }]}
          />
        );
      },
    },
  }], [baseColumns, data, goToApplication, t, lang, history]);

  return (
    <DataTable
      title={t('ats.scorecard.tab.responses')}
      fetching={fetching}
      columns={columns}
      data={data}
      totalRowCount={pagination.totalRowCount}
      pageIndex={pagination.pageIndex}
      pageSize={pagination.pageSize}
      filterList={filterList}
      onFilterConfirm={handleFilterConfirm}
      onTableChanged={handleDataStateChange}
    />
  );
}
